/**
 * IL FORNITORE SULLA PIATTAFORMA (atlante v2, cap. 446; Simone, 29/10/2026): chi vende materiali si iscrive gratis, ma
 * le aziende lo vedono (merci del cantiere, gare, accordi) solo dopo che la piattaforma l'ha approvato. Qui la regola
 * pura: dallo stato scritto a «si vede o no», e quali passaggi di stato sono ammessi.
 *
 * Il dato è `forniture_fornitori/{fornitore}`, scritto solo dal server (console, super utente). Documento assente = in
 * attesa: un fornitore appena iscritto non è stato ancora guardato. È separato dall'accordo economico con la piattaforma
 * (`forniture_accordi_piattaforma`): si può approvare un fornitore prima di fissare la percentuale.
 *
 * Pura: la copiano i codebase del server (`sync:shared` → `_shared-pkg/forniture/`).
 */

export const RACCOLTA_FORNITORI_SULLA_PIATTAFORMA = 'forniture_fornitori';

/** Gli stati scritti. `in_attesa` non si scrive: è l'assenza del documento. */
export const STATI_SCRITTI_DEL_FORNITORE = ['approvato', 'sospeso'] as const;
export type StatoScrittoDelFornitore = (typeof STATI_SCRITTI_DEL_FORNITORE)[number];
export type StatoDelFornitore = 'in_attesa' | StatoScrittoDelFornitore;

/** Un passaggio di stato, per la storia: chi, quando, perché. */
export interface CambioDiStatoDelFornitore {
  stato: StatoScrittoDelFornitore;
  il: string;
  da: string;
  motivo?: string | null;
}

/** `forniture_fornitori/{fornitore}` */
export interface FornitoreSullaPiattaformaDoc {
  stato: StatoScrittoDelFornitore;
  deciso_il: string;
  deciso_da: string;
  motivo?: string | null;
  storia: CambioDiStatoDelFornitore[];
}

/** Lo stato di oggi da un documento letto (o assente). Un valore sconosciuto vale «in attesa»: mai visibile per sbaglio. */
export function statoDelFornitore(doc: Partial<FornitoreSullaPiattaformaDoc> | null | undefined): StatoDelFornitore {
  const s = doc?.stato;
  return s === 'approvato' || s === 'sospeso' ? s : 'in_attesa';
}

/** Le aziende vedono il fornitore? Solo se approvato. */
export const fornitoreVisibile = (doc: Partial<FornitoreSullaPiattaformaDoc> | null | undefined): boolean => statoDelFornitore(doc) === 'approvato';

export type EsitoCambioDiStato = { ok: true } | { ok: false; motivo: 'gia_cosi' | 'motivo_obbligatorio' };

/**
 * Un passaggio è ammesso? Verso lo stato in cui è già, no (non c'è niente da fare); la sospensione vuole il perché
 * (lo legge il fornitore, e resta nella storia).
 */
export function cambioDiStatoAmmesso(attuale: StatoDelFornitore, nuovo: StatoScrittoDelFornitore, motivo: string | null | undefined): EsitoCambioDiStato {
  if (attuale === nuovo) return { ok: false, motivo: 'gia_cosi' };
  if (nuovo === 'sospeso' && !(motivo ?? '').trim()) return { ok: false, motivo: 'motivo_obbligatorio' };
  return { ok: true };
}
